// @ts-strict-ignore
import React, { useContext } from 'react';
import AggregationSelect from './AggregationSelect';
import { StyledRuleClause } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/Rule/RuleTemplate/styles';
import { StyledAggregationSection, StyledAggregationCriteriaRow } from './styles';
import { ContainerType } from '../NodeEditorDrawer/ThresholdsTab/types';
import { getIsPreview } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/helpers';
import { ThresholdContext } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/Rule/Threshold/context';
import { getMultiSelectProps } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/Rule/Threshold/helpers';
import { RuleBuilderContext } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/context';
import { useAppSelector } from '~/store/reduxHooks';
import { selectRuleBuilderActiveSubEditor } from 'flow/store/redux/selectors/ui';

import {
  AGGREGATION_OPTIONS,
  SCOPE_OPTIONS,
  SUBJECT_OPTIONS,
  TIMEFRAME_NUMBER_OPTIONS,
  TIMEFRAME_UNIT_OPTIONS,
} from './constants';
import type { AggregationConfigFormFields, ConfigFormFieldsKey, TimeframeUnit } from './types';
import type { AggregationOption } from './AggregationSelect';

type BaseAggregationsSectionProps = {
  formFields: AggregationConfigFormFields;
  onFormFieldChange: React.Dispatch<React.SetStateAction<AggregationConfigFormFields>>;
};

const BaseAggregationsSection = ({
  formFields,
  onFormFieldChange,
}: BaseAggregationsSectionProps) => {
  const { editMode } = useContext(RuleBuilderContext);
  const { populatedThreshold } = useContext(ThresholdContext);
  const activeSubEditor = useAppSelector(selectRuleBuilderActiveSubEditor);

  const multiSelectProps = getMultiSelectProps({
    action: populatedThreshold?.left?.action,
    options: {
      isPreview: populatedThreshold
        ? getIsPreview(editMode, populatedThreshold, activeSubEditor.thresholdId)
        : false,
    },
  });

  const handleSelectChange = (field: ConfigFormFieldsKey) => (option: AggregationOption) => {
    const { value } = option;
    switch (field) {
      case 'timeframeFrom':
      case 'timeframeTo':
        onFormFieldChange(prev => ({ ...prev, [field]: Number(value) }));
        break;
      case 'timeframeUnit':
        onFormFieldChange(prev => ({ ...prev, [field]: value as TimeframeUnit }));
        break;
      case 'aggregation':
      case 'subject':
      case 'scope':
        onFormFieldChange(prev => ({ ...prev, [field]: value }));
        break;
    }
  };

  return (
    <StyledAggregationSection>
      <StyledAggregationCriteriaRow>
        <AggregationSelect
          multiSelectProps={multiSelectProps}
          options={AGGREGATION_OPTIONS}
          value={formFields.aggregation}
          onChange={handleSelectChange('aggregation')}
        />
        <StyledRuleClause containerType={ContainerType.DRAWER} isEmptyPreviewOutcome={false}>
          of
        </StyledRuleClause>
        <AggregationSelect
          multiSelectProps={multiSelectProps}
          options={SUBJECT_OPTIONS}
          value={formFields.subject}
          onChange={handleSelectChange('subject')}
        />
        <StyledRuleClause containerType={ContainerType.DRAWER} isEmptyPreviewOutcome={false}>
          with the following criteria:
        </StyledRuleClause>
      </StyledAggregationCriteriaRow>

      <StyledAggregationCriteriaRow>
        <StyledRuleClause containerType={ContainerType.DRAWER} isEmptyPreviewOutcome={false}>
          Scope:
        </StyledRuleClause>
        <AggregationSelect
          multiSelectProps={multiSelectProps}
          options={SCOPE_OPTIONS}
          value={formFields.scope}
          onChange={handleSelectChange('scope')}
        />
      </StyledAggregationCriteriaRow>

      <StyledAggregationCriteriaRow>
        <StyledRuleClause containerType={ContainerType.DRAWER} isEmptyPreviewOutcome={false}>
          Timeframe:
        </StyledRuleClause>
        <AggregationSelect
          multiSelectProps={multiSelectProps}
          options={TIMEFRAME_NUMBER_OPTIONS}
          value={String(formFields.timeframeFrom)}
          onChange={handleSelectChange('timeframeFrom')}
        />
        <StyledRuleClause containerType={ContainerType.DRAWER} isEmptyPreviewOutcome={false}>
          to
        </StyledRuleClause>
        <AggregationSelect
          multiSelectProps={multiSelectProps}
          options={TIMEFRAME_NUMBER_OPTIONS}
          value={String(formFields.timeframeTo)}
          onChange={handleSelectChange('timeframeTo')}
        />
        <AggregationSelect
          multiSelectProps={multiSelectProps}
          options={TIMEFRAME_UNIT_OPTIONS}
          value={formFields.timeframeUnit}
          onChange={handleSelectChange('timeframeUnit')}
        />
        <StyledRuleClause containerType={ContainerType.DRAWER} isEmptyPreviewOutcome={false}>
          ago
        </StyledRuleClause>
      </StyledAggregationCriteriaRow>
    </StyledAggregationSection>
  );
};

export default BaseAggregationsSection;
